import { noticeFailure } from "@self/notices";
import { bound, customElement } from "../CustomElement";
import { BillingCheckbox } from "./BillingAddressCheckbox";

/* NOTE This is an equivalent of WooCommerce's checkout.js. For reference, open
 * assets/js/frontend/checkout.js in WooCommerce plugin directory.
 */

interface UpdateCheckoutAction {
	readonly updateShippingMethod: boolean;
}

export interface WcCheckoutParams {
	readonly ajax_url: string;
	readonly apply_coupon_nonce: string;
	readonly checkout_url: string;
	readonly debug_mode: string;
	readonly i18n_checkout_error: string;
	readonly is_checkout: string;
	readonly option_guest_checkout: string;
	readonly remove_coupon_nonce: string;
	readonly update_order_review_nonce: string;
	readonly wc_ajax_url: string;
}

export interface WcAddressI18nParams {
	readonly locale: string;
	readonly locale_fields: string;
}

export interface WcCountrySelectParams {
	readonly countries: string;
	readonly i18n_ajax_error: string;
	readonly i18n_input_too_long_1: string;
	readonly i18n_input_too_long_n: string;
	readonly i18n_input_too_short_1: string;
	readonly i18n_input_too_short_n: string;
	readonly i18n_load_more: string;
	readonly i18n_no_matches: string;
	readonly i18n_searching: string;
	readonly i18n_select_state_text: string;
	readonly i18n_selection_too_long_1: string;
	readonly i18n_selection_too_long_n: string;
}

export interface WcFormField {
	readonly autocomplete: string;
	readonly class: readonly string[];
	readonly label: string;
	readonly priority: number;
	readonly required: boolean;
	readonly label_class?: string;
	readonly hidden?: boolean;
	readonly placeholder?: string;
	readonly type?: string;
	readonly validate?: readonly string[];
}

export interface WcFormFieldWithKey extends WcFormField {
	readonly key: string;
}

export interface WcFormFields {
	readonly [key: string]: WcFormField;
}

export interface WcCountries {
	readonly [countryCode: string]:
	| readonly []
	| { readonly [stateCode: string]: string };
}

export namespace WcAddressI18nParams {
	export interface Locale {
		readonly default: WcFormFields;
		readonly [countryCode: string]: { readonly [key: string]: Partial<WcFormField> };
	}

	export interface LocaleFields {
		readonly [key: string]: string;
	}
}

export interface Option {
	readonly value: string;
	readonly name: string;
}

declare global {
	const wc_checkout_params: WcCheckoutParams;
	const wc_address_i18n_params: WcAddressI18nParams;
	const wc_country_select_params: WcCountrySelectParams;
}

/**
 * NOTE DO NOT EXPECT THIS TO EXIST
 */
declare const jQuery: (selector: string) => any;

function getLocalizedFields(countryCode: string): ReadonlyMap<string, WcFormFieldWithKey> {
	const locale = JSON.parse(wc_address_i18n_params.locale) as WcAddressI18nParams.Locale;

	const fields = new Map<string, WcFormFieldWithKey>();
	const overrides = locale[countryCode];

	for (const key in locale.default) {
		const field: WcFormFieldWithKey = {
			...locale.default[key],
			...overrides?.[key],
			key,
		};

		fields.set(key, Object.freeze(field));
	}

	return fields;
}

function getStatesFor(countryCode: string): readonly Option[] {
	const countries = JSON.parse(wc_country_select_params.countries) as WcCountries;

	const states = Object.entries(countries[countryCode] ?? {}).map(([value, name]) => Object.freeze({ value, name }));
	return Object.freeze(states);
}

@customElement("gct-checkout")
export class Checkout extends HTMLElement {

	form: HTMLFormElement;
	updateTimer: number | undefined;
	xhr: AbortController | undefined;

	processing: boolean;
	paymentTokenChecked: boolean;
	applePaySdkInitialized: boolean;

	constructor() {
		super();

		this.form = null!;
		this.updateTimer = undefined;
		this.xhr = undefined;

		this.processing = false;
		this.paymentTokenChecked = false;
		this.applePaySdkInitialized = false;
	}

	@bound
	setup() {
		this.form = this.querySelector("form")!;

		this.form.addEventListener("submit", this.submit);

		(document.getElementById("delivery-option") as HTMLInputElement).addEventListener("click", this.triggerUpdateCheckout);
		(document.getElementById("pickup-option") as HTMLInputElement).addEventListener("click", this.triggerUpdateCheckout);
		(this.form.elements.namedItem("shipping_country") as HTMLSelectElement).addEventListener("change", this.onCountryChange);
		(this.form.elements.namedItem("shipping_country") as HTMLSelectElement).addEventListener("change", this.triggerUpdateCheckout);
		(this.form.elements.namedItem("billing_country") as HTMLSelectElement).addEventListener("change", this.onCountryChange);

		this.initPaymentMethods();
		this.updateCheckout();

		/* BLINK HACK
		 *
		 * Blink downloads an additional script from
		 * https://secure.blinkpayment.co.uk/assets/js/apple-pay-api.js which
		 * calls `.submit()` on the form when the Apple Pay transaction is
		 * authorized (line 144). This would skip the entire logic in our
		 * `submit` method.
		 */
		this.form.submit = () => {
			this.submit(new SubmitEvent("submit"));
		};
	}

	@bound
	paymentMethodSelected(event: MouseEvent) {
		event.stopPropagation();
	}

	connectedCallback() {
		setTimeout(this.setup, 1000);

		document.addEventListener("click", this.onClickGlobal);
	}

	initPaymentMethods() {
		const paymentMethods = Array.from(this.form.querySelectorAll("input[name=payment_method]") as NodeListOf<HTMLInputElement>);

		for (const input of paymentMethods) {
			input.addEventListener("click", this.paymentMethodSelected);
			input.addEventListener("change", this.triggerUpdateCheckout);
		}

		// If there are none selected, select the first
		let current = paymentMethods.find((p) => p.checked);
		if (current === undefined) {
			current = paymentMethods[0];
			current.checked = true;
		}

		current.click();
	}

	@bound
	triggerUpdateCheckout() {
		this.updateCheckout();
	}

	getValue(name: string): string {
		const element = this.form.elements.namedItem(name) as HTMLInputElement | RadioNodeList | null;
		if (element) {
			// Force a string, just in case the element is not an input and .value is undefined
			return String(element.value ?? "");
		} else {
			return "";
		}
	}

	setInputValue(name: string, value: string) {
		const element = this.form.elements.namedItem(name) as HTMLInputElement | null;
		if (element !== null) {
			element.value = value;
		}
	}

	updateCheckout(args?: UpdateCheckoutAction) {
		// Small timeout to prevent multiple requests when several fields update at the same time
		this.resetUpdateCheckoutTimer();
		this.updateTimer = setTimeout(this.updateCheckoutAction, 5, args);
	}

	resetUpdateCheckoutTimer() {
		clearTimeout(this.updateTimer);
		this.updateTimer = undefined;
	}

	@bound
	async updateCheckoutAction(args: UpdateCheckoutAction = { updateShippingMethod: true }) {
		if (this.xhr !== undefined) {
			this.xhr.abort(new Error("Aborted"));
			this.xhr = undefined;
		}

		const shippingCountry = (document.getElementById("shipping_country") as HTMLInputElement | null)?.value ?? "";
		const shippingState = (document.getElementById("shipping_state") as HTMLSelectElement | null)?.value ?? "";
		const shippingPostcode = (document.getElementById("shipping_postcode") as HTMLInputElement | null)?.value ?? "";
		const shippingCity = (document.getElementById("shipping_city") as HTMLInputElement | null)?.value ?? "";
		const shippingAddress = (document.getElementById("shipping_address_1") as HTMLInputElement | null)?.value ?? "";
		const shippingAddress2 = (document.getElementById("shipping_address_2") as HTMLInputElement | null)?.value ?? "";

		let billingCountry = shippingCountry;
		let billingState = shippingState;
		let billingPostcode = shippingPostcode;
		let billingCity = shippingCity;
		let billingAddress = shippingAddress;
		let billingAddress2 = shippingAddress2;

		const billingCheckbox = this.form.querySelector<BillingCheckbox>("gct-billing-address-checkbox");

		/* NOTE This is not exactly the same as WooCommerce's checkbox. Our says
		 * "Billing address same as delivery address". This means that when it's
		 * unchecked, we must use billing-specific inputs for billing data.
		 */
		if (billingCheckbox === null || !billingCheckbox.checked) {
			billingCountry = (document.getElementById("billing_country") as HTMLInputElement | null)?.value ?? "";
			billingState = (document.getElementById("billing_state") as HTMLSelectElement | null)?.value ?? "";
			billingPostcode = (document.getElementById("billing_postcode") as HTMLInputElement | null)?.value ?? "";
			billingCity = (document.getElementById("billing_city") as HTMLInputElement | null)?.value ?? "";
			billingAddress = (document.getElementById("billing_address_1") as HTMLInputElement | null)?.value ?? "";
			billingAddress2 = (document.getElementById("billing_address_2") as HTMLInputElement | null)?.value ?? "";
		}

		let hasFullAddress = true;
		for (const input of this.form.querySelectorAll("input[data-required], select[data-required]") as NodeListOf<HTMLInputElement | HTMLSelectElement>) {
			if (input.value === "") {
				hasFullAddress = false;
				break;
			}
		}

		const pickup = this.getValue("shipping_method[0]").includes("local_pickup");

		// Assume no files
		const formData = Array.from(new FormData(this.form).entries()) as [string, string][];

		const paymentMethod = this.getValue("payment_method");

		const requestData = new FormData();
		requestData.set("security", wc_checkout_params.update_order_review_nonce);
		requestData.set("payment_method", paymentMethod);
		requestData.set("country", billingCountry);
		requestData.set("state", billingState);
		requestData.set("postcode", billingPostcode);
		requestData.set("city", billingCity);
		requestData.set("address", billingAddress);
		requestData.set("address_2", billingAddress2);
		requestData.set("s_country", pickup ? "GB" : shippingCountry);
		requestData.set("s_state", pickup ? "" : shippingState);
		requestData.set("s_postcode", pickup ? "G75 0YG" : shippingPostcode);
		requestData.set("s_city", pickup ? "East Kilbride" : shippingCity);
		requestData.set("s_address", pickup ? "1–11 Langlands Avenue" : shippingAddress);
		requestData.set("s_address_2", pickup ? "Kelvin South Business Park" : shippingAddress2);
		requestData.set("has_full_address", (pickup || hasFullAddress).toString());
		requestData.set("post_data", new URLSearchParams(formData).toString());

		if (args.updateShippingMethod) {
			requestData.set("shipping_method[0]", this.getValue("shipping_method[0]"));
		}

		this.xhr = new AbortController();

		const spinner = document.getElementById("checkout-spinner");
		spinner?.classList.remove("gct-d-none");

		const res = await fetch(this.dataset.endpoint!, {
			method: "POST",
			body: requestData,
			signal: this.xhr.signal,
		});

		const responseData = await res.json();

		this.xhr = undefined;

		if (typeof responseData === "object" && responseData !== null) {
			if ("reload" in responseData && responseData.reload === true) {
				window.location.reload();
				return;
			}

			if ("fragments" in responseData && typeof responseData.fragments === "object" && responseData.fragments !== null) {
				const { fragments } = responseData;
				if (".woocommerce-checkout-payment" in fragments && typeof fragments[".woocommerce-checkout-payment"] === "string") {
					const parser = new DOMParser();
					const parsedFragment = parser.parseFromString(fragments[".woocommerce-checkout-payment"], "text/html");

					const scriptElements = Array.from(parsedFragment.getElementsByTagName("script"));
					scriptElements.forEach((e) => e.remove());

					// BLINK HACK (one of their scripts expects `$` to be defined globally)
					(window as any).$ = jQuery;

					const container = document.getElementById("gct-payment")!;
					container.replaceChildren(...parsedFragment.body.childNodes);
					for (const scriptElement of scriptElements) {

						/* NOTE We add the SDK script from Apple only once,
						 * because otherwise it tries to redefine a custom
						 * element, causing an error. Either way, the Apple Pay
						 * button seems to work, but by doing it this way we get
						 * rid of console clutter.
						 */
						const url = new URL(scriptElement.src);
						if (url.origin === "https://applepay.cdn-apple.com" && scriptElement.src.endsWith("apple-pay-sdk.js")) {
							if (this.applePaySdkInitialized) {
								continue;
							} else {
								this.applePaySdkInitialized = true;
							}
						}

						const newScriptElement = document.createElement("script");
						newScriptElement.async = scriptElement.async;
						newScriptElement.defer = scriptElement.defer;
						//newScriptElement.integrity = scriptElement.integrity;
						newScriptElement.crossOrigin = scriptElement.crossOrigin;
						newScriptElement.src = scriptElement.src;
						container.appendChild(newScriptElement);
					}
				}

				if (".woocommerce-checkout-review-order-table" in fragments && typeof fragments[".woocommerce-checkout-review-order-table"] === "string") {
					const parser = new DOMParser();
					const parsedFragment = parser.parseFromString(fragments[".woocommerce-checkout-review-order-table"], "text/html");

					const container = document.getElementById("gct-order-review")!;
					container.replaceChildren(...parsedFragment.body.childNodes);
				}
			}

			this.initPaymentMethods();
		}

		spinner?.classList.add("gct-d-none");

		// Blink integration (following the logic in updated_checkout in Blink's custom.js)

		if (paymentMethod === "blink") {
			const paymentBy = this.getValue("payment_by");
			if (paymentBy === "credit-card") {
				document.getElementById("cc_customer_email")?.classList.add("gct-d-none");
				document.querySelectorAll(".blink-form__label.field-label").forEach((e) => {
					if (e.textContent?.trim() === "Email") {
						e.classList.add("gct-d-none");
					}
				});

				try {
					const auto = {
						autoSetup: true,
						autoSubmit: true,
					};

					const $formCard = jQuery('form[name="blink-credit"]');
					const hf = $formCard.hostedForm(auto);
				} catch (error) {
					console.error("An error occured:", error);
				}
			}

			if (this.form.length && paymentBy === "open-banking") {
				this.setInputValue("user_name", this.getValue("billing_first_name") + " " + this.getValue("billing_last_name"));
				this.setInputValue("user_email", this.getValue("billing_email"));
			}

			if (this.form.length && paymentBy === "direct-debit") {
				this.setInputValue("given_name", this.getValue("billing_first_name") + " " + this.getValue("billing_last_name"));
				this.setInputValue("email", this.getValue("billing_email"));
				this.setInputValue("family_name", "");
				this.setInputValue("account_holder_name", "");
			}

			if (document.getElementById("blinkGooglePay") !== null) {
				const scriptElement = document.querySelector("#blinkGooglePay script[src=\"https://pay.google.com/gp/p/js/pay.js\"]") as HTMLScriptElement | null;
				if (scriptElement !== null) {
					const onload = scriptElement.getAttribute("onload") ?? "";
					setTimeout(() => {
						try {
							eval(onload);
						} catch { }
					}, 1000);
				} else {
					console.error("Google Pay script element not found");
				}
			}

			const prefix = billingCheckbox !== null && billingCheckbox.checked ? "shipping_" : "billing_";
			setEachInputValue("customer_name", this.getValue(`${prefix}first_name`) + " " + this.getValue(`${prefix}last_name`));
			setEachInputValue("customer_email", this.getValue("billing_email")); // NOTE There is no shipping email, only billing
			setEachInputValue("customer_address", this.getValue(`${prefix}address_1`) + ", " + this.getValue(`${prefix}address_2`));
			setEachInputValue("customer_postcode", this.getValue(`${prefix}postcode`));
			setEachInputValue("device_timezone", "0");
			setEachInputValue("device_capabilities", "javascript");
			setEachInputValue("device_accept_language", window?.navigator?.language || "");
			setEachInputValue("device_screen_resolution", "0x0x0");
			setEachInputValue("remote_address", "0.0.0.0");

			setupApplePayButtonObserver();
		}

		// End of Blink integration
	}

	@bound
	async submit(event: SubmitEvent) {
		this.resetUpdateCheckoutTimer();
		event.preventDefault();

		if (this.processing) {
			return false;
		}

		const spinner = document.getElementById("checkout-spinner");
		let keepSpinner = false;

		try {
			this.processing = true;
			spinner?.classList.remove("gct-d-none");

			const pickup = this.getValue("shipping_method[0]").includes("local_pickup");
			if (pickup) {
				this.setInputValue("shipping_country", "GB");
				this.setInputValue("shipping_state", "");
				this.setInputValue("shipping_postcode", "G75 0YG");
				this.setInputValue("shipping_city", "East Kilbride");
				this.setInputValue("shipping_address_1", "1–11 Langlands Avenue");
				this.setInputValue("shipping_address_2", "Kelvin South Business Park");
			}

			const billingCheckbox = this.form.querySelector<BillingCheckbox>("gct-billing-address-checkbox");

			const data = new FormData(this.form);

			/* NOTE This is not exactly the same as WooCommerce's checkbox. Our says
			 * "Billing address same as delivery address". This means that when it's
			 * checked, we must use shipping-specific inputs for billing data.
			 *
			 */
			if (billingCheckbox !== null && billingCheckbox.checked) {
				/* NOTE We rely on FormData.set instead of this.setInputValue,
				 * because billing inputs may have a different country, which
				 * would require recreating the state/county input. Also, we
				 * don't want to erase whatever the user put in before ticking
				 * the checkbox in case the checkout fails and the user goes
				 * back to change some things.
				 */
				data.set("billing_first_name", this.getValue("shipping_first_name"));
				data.set("billing_last_name", this.getValue("shipping_last_name"));
				data.set("billing_company", this.getValue("shipping_company"));
				data.set("billing_country", this.getValue("shipping_country"));
				data.set("billing_address_1", this.getValue("shipping_address_1"));
				data.set("billing_address_2", this.getValue("shipping_address_2"));
				data.set("billing_city", this.getValue("shipping_city"));
				data.set("billing_state", this.getValue("shipping_state"));
				data.set("billing_postcode", this.getValue("shipping_postcode"));
				data.set("billing_phone", this.getValue("shipping_phone"));
			}

			// Blink integration

			/* NOTE We use FormData.get instead of this.getValue to take into
			 * account the billing checkbox logic, which happens above.
			 */
			setEachInputValue("customer_name", data.get("billing_first_name") as string + " " + data.get("billing_last_name") as string);
			setEachInputValue("customer_email", data.get("billing_email") as string);
			setEachInputValue("customer_address", data.get("billing_address_1") as string + ", " + data.get("billing_address_2") as string);
			setEachInputValue("customer_postcode", data.get("billing_postcode") as string);

			if (this.getValue("payment_by") === "credit-card") {
				document.getElementById("blink-credit-submit")?.click();

				while (true) {
					await new Promise((resolve) => setTimeout(resolve, 100));

					const childForm = document.querySelector("form[name=blink-credit]") as HTMLFormElement;
					const paymentTokenElement = childForm.elements.namedItem("paymentToken") as HTMLInputElement | null;

					if (paymentTokenElement === null) {
						console.log("Waiting for payment token...");
						continue;
					}

					const childFormData = Array.from(new FormData(childForm).entries()) as [string, string][];
					const paymentData = new URLSearchParams(childFormData).toString();
					data.set("credit-card-data", paymentData);

					break;
				}
			}

			// End of Blink integration

			const res = await fetch(wc_checkout_params.checkout_url, {
				method: "POST",
				body: new URLSearchParams(data as any),
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
			});

			const responseText = await res.text();
			const responseData = parseResponseText(responseText);

			if (responseData !== null) {
				if ("result" in responseData) {
					if (responseData.result === "success") {
					} else if (responseData.result === "failure") {
						let message = "Order failed";
						if ("messages" in responseData && typeof responseData.messages === "string") {
							const document = new DOMParser().parseFromString(responseData.messages, "text/html");
							const textContent = document.body.textContent;
							if (textContent) {
								message = textContent;
							}
						}
						await noticeFailure(message);
					}
				}

				if ("redirect" in responseData && typeof responseData.redirect === "string") {
					keepSpinner = true;
					if (-1 === responseData.redirect.indexOf("https://") || -1 === responseData.redirect.indexOf("http://")) {
						(window as any).location = responseData.redirect;
					} else {
						(window as any).location = decodeURI(responseData.redirect);
					}
				}

				if ("fragments" in responseData && typeof responseData.fragments === "object" && responseData.fragments !== null) {
					const { fragments } = responseData;
					if ("form.woocommerce-checkout" in fragments && typeof fragments["form.woocommerce-checkout"] === "string") {
						const parser = new DOMParser();
						const parsedFragment = parser.parseFromString(fragments["form.woocommerce-checkout"], "text/html");

						// TODO Extract notice text from parsedFragment and call noticeFailure
						// console.dir(parsedFragment);
					}
				}
			}
		} catch {
			await noticeFailure("Order failed");
		} finally {
			if (!keepSpinner) {
				this.processing = false;
				spinner?.classList.add("gct-d-none");
			}
		}
	}

	@bound
	onClickGlobal(event: MouseEvent) {
		const target = event.target as Element | null;

		if (target?.matches("#gpay-button-online-api-id")) {
			this.setInputValue("payment_by", "google-pay");
		}

		if (target?.matches("#selectBatch")) {
			if ((document.getElementById("credit-card") as HTMLInputElement).checked) {
				this.setInputValue("payment_by", "credit-card");
			}

			if ((document.getElementById("direct-debit") as HTMLInputElement).checked) {
				this.setInputValue("payment_by", "direct-debit");
			}

			if ((document.getElementById("open-banking") as HTMLInputElement).checked) {
				this.setInputValue("payment_by", "open-banking");
			}
		}
	}

	@bound
	onCountryChange(event: Event) {
		const target = event.currentTarget as HTMLSelectElement;
		const countryCode = target.value;

		const addressTypePrefix =
			target.name === "shipping_country" ? "shipping_"
				: target.name === "billing_country" ? "billing_"
					: null;

		if (addressTypePrefix === null) {
			console.error(`Unknown address type for country select named ${target.name}`);
			return;
		}

		const fields = getLocalizedFields(countryCode);
		const states = getStatesFor(countryCode);

		const root = addressTypePrefix === "billing_"
			? document.getElementById("checkout-billing")!
			: document.getElementById("checkout-delivery")!;

		// --- CHANGE STATE OPTIONS ---

		const stateField = fields.get("state")!;

		const actualStateType =
			states.length > 0 ? "select"
				: stateField.hidden === true ? "hidden"
					: "text";

		const prevStateInput = this.form.elements.namedItem(addressTypePrefix + "state") as HTMLSelectElement | HTMLInputElement;
		// Hidden inputs have no parent container
		const prevStateContainer = prevStateInput.type === "hidden" ? prevStateInput : prevStateInput.parentNode! as HTMLElement;

		const nextStateInput = makeStateInput({
			type: actualStateType,
			addressTypePrefix,
			required: stateField.required ?? false,
			label: stateField.label,
			options: states,
		});

		prevStateContainer.remove();
		root.appendChild(nextStateInput);

		// --- SORT FIELDS ----

		sortChildren(root, (a, b) => {
			const aname = ((a.querySelector("[name]") as { name: string } | null)?.name ?? "").slice(addressTypePrefix.length);
			const bname = ((b.querySelector("[name]") as { name: string } | null)?.name ?? "").slice(addressTypePrefix.length);

			const aprio = fields.get(aname)?.priority ?? Infinity;
			const bprio = fields.get(bname)?.priority ?? Infinity;

			const diff = aprio - bprio;

			return Number.isNaN(diff) ? 0 : diff;
		});

		// --- APPLY PROPERTIES ---

		/* Properties applied:
		 * - label (as label's textContent)
		 * - required (as boolean data-required attribute on the input)
		 * - hidden (as gct-d-none class on the container; hiding also resets value to "")
		 */

		for (const field of fields.values()) {
			const name = addressTypePrefix + field.key;

			// NOTE Inputs have IDs identical to their names
			const input = document.getElementById(name) as HTMLInputElement | HTMLSelectElement | null;
			const label = root.querySelector(`label[for=${name}]`) as HTMLLabelElement | null;

			if (input !== null) {
				const container = input.type === "hidden" ? input : input.parentNode as HTMLElement;

				if (field.required) {
					input.dataset.required = "";
				} else {
					delete input.dataset.required;
				}

				if (field.hidden === true) {
					container.classList.add("gct-d-none");
					input.value = "";
				} else {
					container.classList.remove("gct-d-none");
				}
			}

			if (label !== null) {
				let { label: textContent } = field;
				if (!field.required) {
					textContent += " (optional)";
				}
				label.textContent = textContent;
			}
		}
	}
}

function sortChildren(root: HTMLElement, compareFn: (a: HTMLElement, b: HTMLElement) => number): void {
	const childNodes = Array.from(root.children) as HTMLElement[];
	childNodes.sort(compareFn);
	root.replaceChildren(...childNodes);
}

function parseResponseText(text: string): object | null {
	try {
		const json: unknown = JSON.parse(text);
		if (typeof json === "object" && json !== null) {
			return json;
		}
	} catch { }

	/* Here's the deal: SOMETIMES instead of JSON you get an HTML as a
	 * response followed by the actual JSON (and the Content-Type in the
	 * response is still application/json). When that happens, we have to
	 * extract the JSON by scanning for it with regex.
	 *
	 * NOTE This is not made up, this is actually in WooCommerce's frontend
	 * code (search for "dataFilter" in
	 * woocommerce/assets/js/frontend/checkout.js).
	 */

	const fixedTextCandidate = text.match(/{"result.*}/);

	if (fixedTextCandidate === null) {
		return null;
	}

	try {
		const json: unknown = JSON.parse(fixedTextCandidate[0]);
		if (typeof json === "object" && json !== null) {
			return json;
		}
	} catch { }

	return null;
}

function setupApplePayButtonObserver() {

	const overrideApplePayButtonClicked = () => {
		const originalOnApplePayButtonClicked = (window as any).onApplePayButtonClicked;
		(window as any).onApplePayButtonClicked = function (this: any, ...args: any[]) {
			((document.querySelector("gct-checkout > form") as HTMLFormElement).elements.namedItem("payment_by") as HTMLInputElement).value = "apple-pay";
			if (typeof originalOnApplePayButtonClicked === "function") {
				originalOnApplePayButtonClicked.apply(this, args);
			}
		};
	};

	const checkApplePayButton = () => {
		if (typeof (window as any).onApplePayButtonClicked === "function") {
			observer.disconnect();
			overrideApplePayButtonClicked();
		}
	};

	const observer = new MutationObserver(checkApplePayButton);
	observer.observe(document, { childList: true, subtree: true });
}

function setEachInputValue(name: string, value: string) {
	document.querySelectorAll(`input[name="${name}"]`).forEach((e) => (e as HTMLInputElement).value = value);
}

namespace makeStateInput {
	export interface CommonArgs {
		readonly addressTypePrefix: "shipping_" | "billing_";
	}

	export interface HiddenArgs extends CommonArgs {
		readonly type: "hidden";
	}

	export interface TextArgs extends CommonArgs {
		readonly type: "text";
		readonly required: boolean;
		readonly label: string;
	}

	export interface SelectArgs extends CommonArgs {
		readonly type: "select";
		readonly required: boolean;
		readonly label: string;
		readonly options: readonly Option[];
	}

	export type Args =
		| HiddenArgs
		| TextArgs
		| SelectArgs
		;
}

function makeStateInput(args: makeStateInput.Args): HTMLElement {
	const name = `${args.addressTypePrefix}state`;
	switch (args.type) {
		case "select": {
			const select = (
				<select id={name} name={name} className="gct-w100" dataset-required={args.required}>
					<option value="">Select an option</option>
					{...args.options.map(({ value, name }) => <option value={value}>{name}</option>)}
				</select>
			) as HTMLSelectElement;

			if (args.required) {
				select.dataset.required = "";
			}

			return (
				<div className="gct-p-relative gct-w100">
					{select}
					<label htmlFor={name}>{args.label}</label>
				</div>
			);
		}
		case "text":
			const input = (
				<input id={name} type="text" name={name} placeholder=" " className="gct-w100" required={args.required} />
			) as HTMLInputElement;

			if (args.required) {
				input.dataset.required = "";
			}

			return (
				<div className="gct-p-relative gct-w100">
					<input id={name} type="text" name={name} placeholder=" " className="gct-w100" required={args.required} />
					<label htmlFor={name}>{args.required ? args.label : `${args.label} (Optional)`}</label>
				</div>
			);
		case "hidden":
			return <input id={name} type="hidden" name={name} value="" />;
	}
}
